import { useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import {
  DateRange,
  NumberFilterDefinition,
  FilterValue,
  OneDataCollection,
} from '@factorialco/f0-react/dist/experimental'
import { F0Dialog } from '@factorialco/f0-react'
import {
  DeleteBudgetMovementDocument,
  FinanceBudgetDetailTrainingResourceDataCollectionDocument,
  TrainingBudgetMovementFragment,
  FinanceBudgetsDetailBudgetFragment,
  FinanceBudgetsBudgetAmountWidgetsFragment,
} from 'generated/resources/graphql'
import { Delete, ExternalLink } from '@factorialco/f0-react/icons/app'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { mapNumberFilterValueToIntegerFilter } from 'modules/finance/lib/mapNumberFilterValueToIntegerFilter'
import { amountWithCurrency } from 'modules/finance/lib/budgets'
import { useTrainingProviderFilter } from 'modules/finance/components/Budgets/BudgetDetailDataCollection/TrainingResourceDataCollection/filters/useTrainingProviderFilter'
import { mapDateRangeToDateFilter } from 'modules/finance/lib/mapDateRangeToDateFilter'
import { paymentStatusVariant, paymentStatusLabel } from 'modules/finance/lib/budgetMovement'
import {
  trainingGroupStatusVariant,
  trainingGroupStatusLabel,
} from 'modules/trainings/lib/trainingGroupStatus'
import { BudgetAmountWidgets } from 'modules/finance/components/Budgets/BudgetDetailDataCollection/AmountWidgets'
import { TrainingGroupDetail } from 'modules/finance/components/Budgets/BudgetDetailDataCollection/TrainingResourceDataCollection/Detail'
import { AddGroupModal } from 'modules/finance/components/Budgets/BudgetDetailDataCollection/TrainingResourceDataCollection/AddGroupModal'

import { Box } from '@factorialco/deprecated-design-system'

type TrainingBudgetedResourceNarrowed = Extract<
  NonNullable<TrainingBudgetMovementFragment['budgetedResource']>,
  { __typename?: 'TrainingsTrainingClass' }
>

function isTrainingClass(
  resource: TrainingBudgetMovementFragment['budgetedResource']
): resource is TrainingBudgetedResourceNarrowed {
  return resource?.__typename === 'TrainingsTrainingClass'
}

export type TrainingBudgetMovement = Omit<TrainingBudgetMovementFragment, 'budgetedResource'> & {
  budgetedResource: TrainingBudgetedResourceNarrowed
  trainingId: number
  trainingName: string
}

interface Props {
  budget: FinanceBudgetsDetailBudgetFragment
  amounts: FinanceBudgetsBudgetAmountWidgetsFragment
  locale: string
}

interface Filters {
  status?: string[]
  trainingProvider?: string[]
  dates?: Date | DateRange
  costs?: FilterValue<NumberFilterDefinition>
  groupStatus?: string[]
}

/**
 * Maps the filters state to the GraphQL filter format
 *
 * @param filters - The filters state from the data collection
 * @returns The GraphQL filter object with the following fields:
 *   - amountCents: IntegerFilter
 *   - status: StringFilter
 *   - trainingProvider: StringFilter
 *   - trainingClassStartDate: DateFilter
 *   - trainingClassEndDate: DateFilter
 *   - trainingClassStatus: StringFilter
 */
const mapFiltersToGraphqlFilters = (
  filters: Filters,
  { excludeStatus = false }: { excludeStatus?: boolean } = {}
) => {
  const costsFilter = mapNumberFilterValueToIntegerFilter(filters.costs, true)
  const trainingClassStartDateFilter = mapDateRangeToDateFilter(filters.dates, { from: 'gteq' })
  const trainingClassEndDateFilter = mapDateRangeToDateFilter(filters.dates, { to: 'lteq' })

  const withStatusFilter = !excludeStatus && !!filters.status?.length
  return {
    ...(withStatusFilter && {
      status: { in: filters.status },
    }),
    ...(!!filters.trainingProvider?.length && {
      trainingProvider: { in: filters.trainingProvider },
    }),
    ...(!!trainingClassStartDateFilter && {
      trainingClassStartDate: trainingClassStartDateFilter,
    }),
    ...(!!trainingClassEndDateFilter && {
      trainingClassEndDate: trainingClassEndDateFilter,
    }),
    ...(!!costsFilter && {
      amountCents: costsFilter,
    }),
    ...(!!filters.groupStatus?.length && {
      trainingClassStatus: { in: filters.groupStatus },
    }),
  }
}

export const TrainingResourceDataCollection = ({ budget, amounts, locale }: Props) => {
  const trainingNamesMap = useRef<Record<number, string>>({})
  const [selectedItem, setSelectedItem] = useState<TrainingBudgetMovement | null>(null)
  const [itemToRemove, setItemToRemove] = useState<TrainingBudgetMovement | null>(null)
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [deleteBudgetMovement] = useMutation(DeleteBudgetMovementDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceBudgetMovement,
    refetchQueries: [
      'FinanceBudgetDetailTrainingResourceDataCollection',
      'FinanceBudgetDetailRevampPage',
      'FinanceBudgetDetailAmountWidgets',
    ],
  })
  const trainingProviderFilter = useTrainingProviderFilter(
    i18n.t('trainings.trainings_revamp.training_detail.overview.provider')
  )
  const source = useGraphqlDataCollectionSource({
    document: FinanceBudgetDetailTrainingResourceDataCollectionDocument,
    mapData: (data): TrainingBudgetMovement[] => {
      const items = data.finance.budgetMovementsConnection.edges.map((edge) => {
        const node = edge.node
        const budgetedResource = node.budgetedResource

        if (!isTrainingClass(budgetedResource)) {
          throw new Error('Expected TrainingsTrainingClass')
        }

        const trainingId = budgetedResource.training?.id || 0
        const trainingName = budgetedResource.training?.name || 'Unknown'

        if (trainingId !== null) {
          trainingNamesMap.current[trainingId] = trainingName
        }

        return {
          ...node,
          budgetedResource,
          trainingId,
          trainingName,
        }
      })

      return items
    },
    mapPagination: (data) => ({
      total: data.finance.budgetMovementsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ filters }) => {
      return {
        budgetId: budget.id,
        sortOrder: {
          field: 'trainingName',
          order: 'asc' as const,
        },
        filter: mapFiltersToGraphqlFilters(filters),
      }
    },
    filters: {
      groupStatus: {
        label: i18n.t('budgets.detail_training_budget.columns.group_status'),
        type: 'in',
        options: {
          options: [
            { label: trainingGroupStatusLabel('planned'), value: 'planned' },
            { label: trainingGroupStatusLabel('ongoing'), value: 'ongoing' },
            { label: trainingGroupStatusLabel('completed'), value: 'completed' },
          ],
        },
      },
      dates: {
        label: i18n.t('budgets.detail_training_budget.filters.dates'),
        type: 'date' as const,
        options: {
          mode: 'range' as const,
          view: 'day' as const,
        },
      },
      costs: {
        type: 'number',
        label: i18n.t('budgets.detail_training_budget.columns.group_cost'),
        options: {
          modes: ['range', 'single'],
          min: 0,
          openCloseToggle: true,
        },
      },
      trainingProvider: trainingProviderFilter,
      status: {
        label: i18n.t('budgets.detail_training_budget.columns.payment_status'),
        type: 'in',
        options: {
          options: [
            { label: paymentStatusLabel('pending'), value: 'pending' },
            { label: paymentStatusLabel('spent'), value: 'spent' },
          ],
        },
      },
    },
    presets: [
      { label: paymentStatusLabel('pending'), filter: { status: ['pending'] } },
      { label: paymentStatusLabel('spent'), filter: { status: ['spent'] } },
    ],
    grouping: {
      collapsible: true,
      mandatory: true,
      defaultOpenGroups: true,
      groupBy: {
        trainingId: {
          name: 'Training',
          label: (groupId) =>
            trainingNamesMap.current[groupId] ||
            i18n.t('budgets.detail_training_budget.columns.no_training'),
          defaultDirection: 'asc' as const,
        },
      },
    },
    secondaryActions: {
      expanded: 1,
      actions: () => [
        {
          label: i18n.t('budgets.add_group_modal.add_training_group'),
          onClick: () => setIsAddGroupModalOpen(true),
          disabled: budget.archived,
        },
      ],
    },
    itemOnClick: (item: TrainingBudgetMovement) => () => setSelectedItem(item),
    totalItemSummary: true,
    itemActions: (item) => [
      {
        label: i18n.t('budgets.detail_training_budget.row_actions.go_to_training_group'),
        icon: ExternalLink,
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.trainingClasses.detail.costs.fullPath({
              id: String(item.trainingId),
              classId: String(item.budgetedResource.id),
            })
          )
        },
      },
      {
        label: i18n.t('budgets.detail_training_budget.row_actions.remove_group_cost'),
        icon: Delete,
        critical: true,
        onClick: () => setItemToRemove(item),
      },
    ],
  })

  const visualizations = [
    {
      type: 'table' as const,
      options: {
        columns: [
          {
            label: i18n.t('budgets.detail_training_budget.columns.training_group'),
            render: (item: TrainingBudgetMovement) => ({
              type: 'text' as const,
              value: item.budgetedResource.name ?? '-',
            }),
          },
          {
            label: i18n.t('budgets.detail_training_budget.columns.group_status'),
            render: (item: TrainingBudgetMovement) => {
              const status = item.budgetedResource.status ?? 'planned'
              return {
                type: 'status' as const,
                value: {
                  label: trainingGroupStatusLabel(status),
                  status: trainingGroupStatusVariant(status),
                },
              }
            },
          },
          {
            label: i18n.t('budgets.detail_training_budget.columns.group_start_date'),
            render: (item: TrainingBudgetMovement) => ({
              type: 'text' as const,
              value: item.budgetedResource.startDate
                ? dateBackendToUI(item.budgetedResource.startDate)
                : '-',
            }),
          },
          {
            label: i18n.t('budgets.detail_training_budget.columns.group_end_date'),
            render: (item: TrainingBudgetMovement) => ({
              type: 'text' as const,
              value: item.budgetedResource.endDate
                ? dateBackendToUI(item.budgetedResource.endDate)
                : '-',
            }),
          },
          {
            label: i18n.t('budgets.detail_training_budget.columns.group_cost'),
            render: (item: TrainingBudgetMovement) => ({
              type: 'text' as const,
              value: item.amountCents
                ? amountWithCurrency(Number(item.amountCents), item.sourceCurrency, locale)
                : '-',
            }),
          },
          {
            label: i18n.t('budgets.detail_training_budget.columns.training_provider'),
            render: (item: TrainingBudgetMovement) => ({
              type: 'text' as const,
              value: item.budgetedResource.training?.external
                ? item.budgetedResource.training.externalProvider || '-'
                : i18n.t('budgets.detail_training_budget.columns.internal_provider'),
            }),
          },

          {
            label: i18n.t('budgets.detail_training_budget.columns.payment_status'),
            render: (item: TrainingBudgetMovement) => {
              const status = item.status ?? 'draft'
              return {
                type: 'status' as const,
                value: {
                  label: paymentStatusLabel(status),
                  status: paymentStatusVariant(status),
                },
              }
            },
          },
          {
            label: i18n.t('budgets.detail_training_budget.columns.training_participants'),
            render: (item: TrainingBudgetMovement) => ({
              type: 'text' as const,
              value: item.budgetedResource.classMembershipsConnection.totalCount ?? '0',
            }),
          },
        ],
      },
    },
  ]

  const emptyStates = budget.archived
    ? {
        'no-data': {
          title: i18n.t('budgets.detail_training_budget.empty_state.title'),
          description: '',
          emoji: '📚',
        },
      }
    : {
        'no-data': {
          title: i18n.t('budgets.detail_training_budget.empty_state.title'),
          description: '',
          emoji: '📚',
          actions: [
            {
              label: i18n.t('budgets.add_group_modal.add_training_group'),
              onClick: () => setIsAddGroupModalOpen(true),
            },
          ],
        },
      }

  return (
    <Box flexDirection="column" gap="s24">
      <BudgetAmountWidgets
        budget={budget}
        initialAmounts={amounts}
        filters={mapFiltersToGraphqlFilters(source.currentFilters, { excludeStatus: true })}
        status={source.currentFilters.status?.[0]}
      />
      <OneDataCollection
        source={source}
        visualizations={visualizations}
        emptyStates={emptyStates}
      />
      {selectedItem && (
        <TrainingGroupDetail
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          locale={locale}
        />
      )}
      {isAddGroupModalOpen && (
        <AddGroupModal
          budgetId={budget.id}
          currency={budget.currency}
          availableAmountCents={amounts.availableAmountCents}
          locale={locale}
          onClose={() => setIsAddGroupModalOpen(false)}
        />
      )}
      <F0Dialog
        isOpen={itemToRemove !== null}
        onClose={() => setItemToRemove(null)}
        title={i18n.t('budgets.detail_training_budget.row_actions.remove_from_budget_title')}
        width="sm"
        primaryAction={{
          label: i18n.t('budgets.detail_training_budget.row_actions.remove_from_budget_confirm'),
          icon: Delete,
          onClick: async () => {
            if (!itemToRemove) return
            await deleteBudgetMovement({ variables: { ids: [Number(itemToRemove.id)] } })
            setItemToRemove(null)
          },
          variant: 'negative',
        }}
        secondaryAction={{
          label: i18n.t('common.cancel'),
          onClick: () => setItemToRemove(null),
        }}
      >
        {i18n.t('budgets.detail_training_budget.row_actions.remove_from_budget_description')}
      </F0Dialog>
    </Box>
  )
}
